
import React from 'react';
import { motion } from 'framer-motion';
import { Client } from './Client';
import { ShieldCheck, Lightbulb, Users, Globe } from 'lucide-react';
import CountUp from 'react-countup';

const fadeIn = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.25,
      delayChildren: 0.2,
    },
  },
};

const About = () => {
  return (
    <>
      {/* Who We Are Section */}
      <section className="relative py-24 bg-gradient-to-br from-green-50 via-white to-green-100">
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid md:grid-cols-2 gap-14 items-center">
            {/* Text */}
            <motion.div
              variants={fadeIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false }}
            >
              <span className="text-sm uppercase tracking-widest text-green-600 font-semibold mb-2 block">
                Who We Are
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-snug">
                Your Trusted Partner for Customs Consulting & EPR Compliance
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                <strong className="text-green-700">Environature Solution</strong> is committed to delivering customized,
                impactful solutions across a wide range of industries. We prioritize quality,
                integrity, and your success.
              </p>
              <p className="text-gray-700 leading-relaxed">
                From Waste Management to Electronics, Chemicals, Medical Devices, and more—our
                experienced team provides strategic support to help you navigate the complexities of
                modern compliance. We offer services such as <strong>ISI Mark Certification, BIS Registration, WPC approval, FMCS, BEE Registration, EPR for Plastic & E-Waste, LMPC, FSSAI License, AIS Certification, Gold Hallmarking</strong>, and more.
              </p>
            </motion.div>

            {/* Image */}
            <motion.div
              className="hidden md:block"
              variants={fadeIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false }}
              transition={{ delay: 0.3 }}
            >
              <img
                src="/images/services/custome.jpg"
                alt="About Environature"
                className="rounded-2xl shadow-xl w-full h-[350px] object-cover"
              />
            </motion.div>
          </div>

          {/* Features */}
          <motion.div
            className="mt-16 grid gap-6 md:grid-cols-3"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false }}
          >
            {[
              {
                icon: <ShieldCheck className="text-green-600 w-6 h-6" />,
                title: "Certified Expertise",
                desc: "Years of proven success across regulatory frameworks.",
              },
              {
                icon: <Lightbulb className="text-green-600 w-6 h-6" />,
                title: "Innovative Approach",
                desc: "We adapt to challenges with smart, scalable solutions.",
              },
              {
                icon: <Users className="text-green-600 w-6 h-6" />,
                title: "Client-First Focus",
                desc: "Every strategy is centered around your goals.",
              },
            ].map(({ icon, title, desc }, idx) => (
              <motion.div
                key={idx}
                className="flex items-start gap-4 p-4 rounded-xl bg-white shadow-sm hover:shadow-md transition hover:-translate-y-1"
              >
                <div className="bg-green-100 p-2 rounded-lg transition hover:rotate-6 duration-300">
                  {icon}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{title}</h4>
                  <p className="text-sm text-gray-600">{desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="mt-20 grid md:grid-cols-3 gap-8 text-center">
            {[
              { label: "Clients Served", value: 500 },
              { label: "Certifications Delivered", value: 1200 },
              { label: "Countries Covered", value: 18 },
            ].map((stat, idx) => (
              <div key={idx}>
                <h4 className="text-4xl font-bold text-green-600">
                  <CountUp end={parseInt(stat.value)} duration={10} separator="," suffix="+" />
                </h4>
                <p className="text-gray-600 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            variants={fadeIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false }}
            className="text-center mb-16"
          >
            <span className="text-sm uppercase tracking-widest text-green-600 font-semibold mb-2 block">
              Our Purpose
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              More Than Compliance — We Enable Business Growth
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-10">
            {/* Mission */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false }}
              className="bg-green-50 rounded-2xl p-8 shadow-md"
            >
              <div className="flex items-center mb-6">
                <div className="bg-green-100 p-3 rounded-full mr-4">
                  <Lightbulb className="text-green-600 w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Our Mission</h3>
              </div>
              <p className="text-gray-700 mb-6">
                To simplify complex regulatory requirements and empower businesses to focus on what they do best—innovating and growing their operations.
              </p>
              <ul className="space-y-4">
                {[
                  "Demystify compliance processes",
                  "Provide tailored solutions for each client",
                  "Stay ahead of regulatory changes",
                ].map((point, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Vision */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false }}
              className="bg-green-50 rounded-2xl p-8 shadow-md"
            >
              <div className="flex items-center mb-6">
                <div className="bg-green-100 p-3 rounded-full mr-4">
                  <Globe className="text-green-600 w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Our Vision</h3>
              </div>
              <p className="text-gray-700 mb-6">
                To become the most trusted compliance partner for businesses expanding in India and international markets.
              </p>
              <ul className="space-y-4">
                {[
                  "Expand our service offerings to 20+ countries by 2025",
                  "Develop AI-powered compliance tracking tools",
                  "Reduce average certification time by 30%",
                ].map((point, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="text-green-500 mr-2">✓</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          
        </div>
          {/* CTA */}
        <section className=" mt-10 bg-gradient-to-r from-[#007c82] to-[#00a09c] py-16 text-white">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl font-bold mb-4">Need Help With Regulatory Compliance?</h2>
              <p className="text-xl mb-4 max-w-2xl mx-auto">
                Our team of experts is ready to guide you through every step of the process.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                <a
                  href="/contact"
                  className="inline-block mt-2 bg-green-600 text-white px-6 py-3 rounded-lg shadow hover:bg-green-700 transition"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </section>
      </section>
      {/* Client Section */}
      <Client />
    </>
  );
};

export default About;
